var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useContext, useMemo } from "react";
import { getAssets, getDisplayAssets, isSolanaVault, } from "../constants/constants";
import { defaultLiquidityGaugeV5PoolData, defaultLiquidityMiningPoolData, } from "../models/staking";
import { defaultV2VaultData, defaultVaultData, defaultSolanaVaultData, } from "../models/vault";
import { getAssetDecimals } from "../utils/asset";
import useFetchAssetBalanceData, { defaultUserAssetBalanceData, } from "./useFetchAssetBalanceData";
import useFetchLiquidityMiningData from "./useFetchLiquidityMiningData";
import useFetchV2VaultData from "./useFetchV2VaultData";
import useFetchEarnVaultData from "./useFetchEarnVaultData";
import useFetchVaultData from "./useFetchVaultData";
import { defaultLidoOracleData, useFetchLidoOracleData, } from "./useLidoOracle";
import useFetchTreasuryBalanceData, { defaultTreasuryBalanceData, } from "./useFetchTreasuryBalanceData";
import useFetchLiquidityGaugeV5Data from "./useFetchLiquidityGaugeV5Data";
import useFetchSolVaultData from "./useFetchSolVaultData";
export var Web3DataContext = React.createContext({
    v1: defaultVaultData,
    v2: defaultV2VaultData,
    earn: defaultV2VaultData,
    solana: defaultSolanaVaultData,
    assetBalance: defaultUserAssetBalanceData,
    liquidityMiningPool: defaultLiquidityMiningPoolData,
    liquidityGaugeV5Pool: defaultLiquidityGaugeV5PoolData,
    lidoOracle: defaultLidoOracleData,
    treasuryBalance: defaultTreasuryBalanceData,
});
export var useVaultsData = function () {
    var contextData = useContext(Web3DataContext);
    return {
        data: contextData.v1.responses,
        loading: contextData.v1.loading,
    };
};
export var useVaultData = function (vault) {
    var contextData = useContext(Web3DataContext);
    return __assign(__assign({}, contextData.v1.responses[vault]), { asset: getAssets(vault), displayAsset: getDisplayAssets(vault), decimals: getAssetDecimals(getAssets(vault)), userAssetBalance: contextData.assetBalance.data[getAssets(vault)], status: contextData.v1.loading || contextData.assetBalance.loading
            ? "loading"
            : "success" });
};
export var useV2VaultsData = function () {
    var contextData = useContext(Web3DataContext);
    var data = useMemo(function () { return (__assign(__assign(__assign({}, contextData.v2.responses), contextData.earn.responses), contextData.solana.responses)); }, [
        contextData.v2.responses,
        contextData.earn.responses,
        contextData.solana.responses,
    ]);
    return {
        data: data,
        // be pessimistic for loading, if one is still loading, all will be loading
        loading: contextData.v2.loading ||
            contextData.earn.loading ||
            contextData.solana.loading,
    };
};
export var useV2VaultData = function (vault) {
    var contextData = useContext(Web3DataContext);
    var data = useMemo(function () { return (__assign(__assign(__assign({}, contextData.v2.responses), contextData.earn.responses), contextData.solana.responses)); }, [
        contextData.v2.responses,
        contextData.earn.responses,
        contextData.solana.responses,
    ]);
    var loading = (isSolanaVault(vault)
        ? contextData.solana.loading
        : contextData.v2.loading) || contextData.assetBalance.loading;
    return {
        data: __assign(__assign({}, data[vault]), { asset: getAssets(vault), displayAsset: getDisplayAssets(vault), decimals: getAssetDecimals(getAssets(vault)) }),
        loading: loading,
    };
};
export var useLiquidityMiningPoolData = function (vault) {
    var contextData = useContext(Web3DataContext);
    return {
        data: contextData.liquidityMiningPool.responses[vault],
        loading: contextData.liquidityMiningPool.loading,
    };
};
export var useAllLiquidityGaugeV5PoolsData = function () {
    var contextData = useContext(Web3DataContext);
    return {
        data: contextData.liquidityGaugeV5Pool.responses,
        loading: contextData.liquidityGaugeV5Pool.loading,
    };
};
export var useLiquidityGaugeV5PoolData = function (vault) {
    var contextData = useContext(Web3DataContext);
    return {
        data: contextData.liquidityGaugeV5Pool.responses[vault],
        loading: contextData.liquidityGaugeV5Pool.loading,
    };
};
export var useAssetBalance = function (asset) {
    var contextData = useContext(Web3DataContext);
    return {
        balance: contextData.assetBalance.data[asset],
        loading: contextData.assetBalance.loading,
    };
};
export var useAssetsBalance = function () {
    var contextData = useContext(Web3DataContext);
    return contextData.assetBalance;
};
export var useTreasuryBalance = function () {
    var contextData = useContext(Web3DataContext);
    return contextData.treasuryBalance;
};
export var Web3DataContextProvider = function (_a) {
    var children = _a.children;
    var vaultData = useFetchVaultData();
    var v2VaultData = useFetchV2VaultData();
    var earnVaultData = useFetchEarnVaultData();
    var solVaultData = useFetchSolVaultData();
    var assetBalance = useFetchAssetBalanceData();
    var liquidityGaugeV5Pool = useFetchLiquidityGaugeV5Data();
    var liquidityMiningPool = useFetchLiquidityMiningData();
    var lidoOracle = useFetchLidoOracleData();
    var treasuryBalance = useFetchTreasuryBalanceData();
    return (_jsx(Web3DataContext.Provider, __assign({ value: {
            v1: vaultData,
            v2: v2VaultData,
            earn: earnVaultData,
            solana: solVaultData,
            assetBalance: assetBalance,
            liquidityGaugeV5Pool: liquidityGaugeV5Pool,
            liquidityMiningPool: liquidityMiningPool,
            lidoOracle: lidoOracle,
            treasuryBalance: treasuryBalance,
        } }, { children: children }), void 0));
};
//# sourceMappingURL=web3DataContext.js.map